import { el, tel } from "./dom.ts";
import { t, getLang, setLang, applyTranslations, type Lang } from "./i18n.ts";
import { missingCapabilities } from "./capability.ts";
import { createHelpPanel } from "./faq-panel.ts";

/**
 * Page shell: header, language toggle, and the container panels mount into.
 * Also owns the hard stop for unsupported browsers.
 */

/**
 * Flags are inline SVG, not emoji.
 *
 * Windows ships no country flags in its emoji font, so 🇫🇷 renders as the bare
 * letters "FR" in Chrome and Edge there — and Windows is the primary platform
 * (§5). These are a few hundred bytes each, need no network request, and look
 * the same everywhere.
 */
const FLAG_SVG: Record<Lang, string> = {
    fr: `<svg viewBox="0 0 3 2" aria-hidden="true" focusable="false"><rect width="3" height="2" fill="#ED2939"/><rect width="2" height="2" fill="#fff"/><rect width="1" height="2" fill="#002395"/></svg>`,
    en: `<svg viewBox="0 0 60 30" aria-hidden="true" focusable="false"><clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath><path d="M0,0 v30 h60 v-30 z" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6" clip-path="url(#s)"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" stroke-width="4" clip-path="url(#s)"/><path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10" clip-path="url(#s)"/><path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6" clip-path="url(#s)"/></svg>`
};

/** An original app mark (not a Microsoft logo): documents flowing into a folder. */
const BRAND_MARK_SVG = `<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><rect width="32" height="32" rx="8" fill="var(--accent)"/><path d="M9 12.5A1.5 1.5 0 0 1 10.5 11h4l1.4 1.8H22a1.5 1.5 0 0 1 1.5 1.5v6.2A1.5 1.5 0 0 1 22 22H10.5A1.5 1.5 0 0 1 9 20.5z" fill="none" stroke="#fff" stroke-width="1.6"/><path d="M16 14v4M13.8 15.9 16 18.1l2.2-2.2" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** A shield, for the "nothing leaves your browser" reassurance line. */
const PRIVACY_ICON_SVG = `<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 1.5 3 3.2v4.1c0 3 2.1 5.4 5 6.2 2.9-.8 5-3.2 5-6.2V3.2z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M5.8 8 7.3 9.6 10.4 6.4" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function iconSpan(className: string, svg: string): HTMLSpanElement {
    const span = document.createElement("span");
    span.className = className;
    span.innerHTML = svg;
    return span;
}

const BRAND_MARK = iconSpan("brand-mark", BRAND_MARK_SVG);
const PRIVACY_ICON = iconSpan("privacy-icon", PRIVACY_ICON_SVG);

function langSwitch(): HTMLElement {
    const wrap = el("div", { class: "lang-switch", role: "group", "aria-label": t("lang.label") });

    const makeButton = (lang: Lang, key: "lang.en" | "lang.fr") => {
        const button = el("button", {
            type: "button",
            class: "lang-button",
            "aria-pressed": String(getLang() === lang),
            // The flag is decorative; the accessible name carries the meaning,
            // since a flag is a country and not a language.
            "aria-label": t(key),
            title: t(key)
        });

        const flag = el("span", { class: "flag" });
        flag.innerHTML = FLAG_SVG[lang];
        button.append(flag);

        button.addEventListener("click", () => {
            if (getLang() === lang) {
                return;
            }
            setLang(lang);
            for (const other of wrap.querySelectorAll("button")) {
                other.setAttribute("aria-pressed", "false");
            }
            button.setAttribute("aria-pressed", "true");
            wrap.setAttribute("aria-label", t("lang.label"));
            for (const [otherLang, otherButton] of buttons) {
                otherButton.setAttribute("aria-label", t(otherLang === "fr" ? "lang.fr" : "lang.en"));
                otherButton.setAttribute("title", t(otherLang === "fr" ? "lang.fr" : "lang.en"));
            }
        });
        return button;
    };

    const buttons: [Lang, HTMLElement][] = [
        ["fr", makeButton("fr", "lang.fr")],
        ["en", makeButton("en", "lang.en")]
    ];

    wrap.append(...buttons.map(([, button]) => button));
    return wrap;
}

/**
 * Chromium-only. Rendered instead of the app, not alongside it — a partially
 * working export is worse than a clear refusal (ARCHITECTURE.md §5).
 */
function unsupportedScreen(missing: ReturnType<typeof missingCapabilities>): HTMLElement {
    const list = el("ul");
    for (const key of missing) {
        list.append(el("li", {}, [t("browser.unsupported.missing", { feature: t(key) })]));
    }

    return el("div", { class: "unsupported" }, [
        tel("h1", "browser.unsupported.title"),
        tel("p", "browser.unsupported.body"),
        list
    ]);
}

/**
 * Renders the shell into #app. Returns the container for panels, or null when
 * the browser cannot run the app at all.
 */
export function renderShell(): HTMLElement | null {
    const app = document.querySelector<HTMLElement>("#app");
    if (!app) {
        throw new Error("#app container missing from index.html");
    }
    app.replaceChildren();

    const missing = missingCapabilities();
    if (missing.length > 0) {
        app.append(unsupportedScreen(missing));
        applyTranslations(app);
        return null;
    }

    const header = el("header", { class: "app-header" }, [
        el("div", { class: "app-header-top" }, [
            el("div", { class: "brand" }, [BRAND_MARK, tel("h1", "app.title")]),
            langSwitch()
        ]),
        tel("p", "app.tagline", { class: "app-tagline" }),
        el("p", { class: "privacy-badge" }, [PRIVACY_ICON, tel("span", "app.privacy")])
    ]);

    const panels = el("div", { id: "panels" });

    // Single centred column: sign in → choose → export → browse, then help.
    // The help block is a closed <details> at the very bottom so it never
    // competes with the flow above it (faq-panel.ts).
    const main = el("main", { class: "app-main" }, [header, panels, createHelpPanel()]);

    app.append(main);
    applyTranslations(app);
    return panels;
}
